import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import Modal from 'src/components/layouts/Modal/Modal';
import Icon, { IconName } from 'src/components/ui/Icon/Icon';
import { useEventListeners, useOutsideClick } from 'src/hooks/utils';
import { useRouteAccess } from 'src/hooks/useRouteAccess';
import { useGlobalSearch } from './useGlobalSearch';
import { RecentEntry, useRecentlyVisited } from './useRecentlyVisited';
import { NAV_PAGES } from './navPages';

const MAX_PAGE_HITS = 6;

const DEBOUNCE_MS = 250;

type SelectableItem = {
  to: string;
  label: string;
  subtitle?: string;
  icon: IconName;
};

function KbdChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="px-6 py-2 rounded-base border border-subtle typo-text-12 text-faded leading-none">{children}</span>
  );
}

/**
 * Global Cmd/Ctrl+K command palette ("All-Suche"). Self-contained: owns its open
 * state and the global hotkey, fans the search term out across all entity types
 * (see useGlobalSearch) and navigates to the selected record's detail overlay.
 */
const CommandPalette: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  // Mirror `open` into a ref so the once-registered global key listener reads
  // the current value without a stale closure.
  const openRef = useRef(open);
  openRef.current = open;

  const { recent, push } = useRecentlyVisited();
  const { hasAccess, hasAccessIncludes } = useRouteAccess();

  // Global keys (registered once): Cmd/Ctrl+K toggles, Esc closes. Handled
  // globally rather than on the input because the Modal's focus-trap can hold
  // focus, so an input-level Esc handler would not fire reliably.
  useEventListeners([{ event: 'keydown' }], event => {
    const e = event as KeyboardEvent;
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      setOpen(prev => !prev);
      return;
    }
    if (e.key === 'Escape' && openRef.current) {
      setOpen(false);
    }
  });

  // Close on click outside the palette panel. The shared Modal's backdrop does
  // not wire its full-screen overlay to onClose, so we detect it ourselves.
  useOutsideClick(panelRef, () => setOpen(false));

  // Debounce the term that drives the fan-out queries.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(search), DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [search]);

  // Reset transient state whenever the palette opens/closes.
  useEffect(() => {
    if (open) {
      setActiveIndex(0);
      inputRef.current?.focus();
    } else {
      setSearch('');
      setDebounced('');
      setActiveIndex(0);
    }
  }, [open]);

  const { enabled, isFetching, categories } = useGlobalSearch(debounced, open);

  // The flat, keyboard-navigable list (results while searching, otherwise the
  // recently-visited entries) plus the grouped data used for rendering.
  const recentItems: SelectableItem[] = useMemo(
    () => recent.map((r: RecentEntry) => ({ to: r.to, label: r.label, subtitle: r.subtitle, icon: r.icon })),
    [recent],
  );
  // Static menu pages matching the term (e.g. "Auslieferungen" → the Outbound
  // page itself), shown as their own group above the record results.
  const navMatches: SelectableItem[] = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    if (!q) return [];
    const canAccess = (p: (typeof NAV_PAGES)[number]): boolean => {
      if (!p.access) return hasAccess(p.link);
      if (p.access.kind === 'always') return true;
      if (p.access.kind === 'includes') return hasAccessIncludes(p.access.token);
      return hasAccess(p.access.link);
    };
    return NAV_PAGES.filter(canAccess)
      .map(p => ({ to: `/${p.link}`, label: t(p.labelKey), icon: p.icon }))
      .filter(p => p.label.toLowerCase().includes(q))
      .slice(0, MAX_PAGE_HITS);
  }, [debounced, t, hasAccess, hasAccessIncludes]);

  const selectable: SelectableItem[] = useMemo(() => {
    if (enabled) {
      return [
        ...navMatches,
        ...categories.flatMap(c =>
          c.hits.map(h => ({ to: h.to, label: h.label, subtitle: h.subtitle, icon: c.category.icon })),
        ),
      ];
    }
    return recentItems;
  }, [enabled, navMatches, categories, recentItems]);

  // Keep the highlight within bounds when results arrive asynchronously.
  useEffect(() => {
    setActiveIndex(i => (i > selectable.length - 1 ? 0 : i));
  }, [selectable.length]);

  const select = (item: SelectableItem) => {
    push(item);
    setOpen(false);
    navigate(item.to);
  };

  const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!selectable.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, selectable.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = selectable[activeIndex];
      if (item) select(item);
    }
  };

  // Flat index per rendered row, so the keyboard highlight lines up with groups.
  let flatIndex = -1;
  const renderRow = (item: SelectableItem, key: string) => {
    flatIndex += 1;
    const index = flatIndex;
    const isActive = index === activeIndex;
    return (
      <button
        key={key}
        type="button"
        ref={isActive ? el => el?.scrollIntoView({ block: 'nearest' }) : undefined}
        onMouseMove={() => setActiveIndex(index)}
        onClick={() => select(item)}
        className={classNames(
          'w-full flex items-center gap-12 px-16 py-10 text-left rounded-base',
          isActive ? 'bg-surface-hover' : '',
        )}>
        <Icon name={item.icon} width={18} height={18} className="text-faded shrink-0" />
        <span className="flex-1 min-w-0 truncate typo-text-14 text-full">{item.label}</span>
        {item.subtitle && (
          <span className="shrink-0 truncate typo-text-12 text-faded max-w-[45%]">{item.subtitle}</span>
        )}
        {isActive && <KbdChip>Enter</KbdChip>}
      </button>
    );
  };

  return (
    <Modal isOpen={open} onClose={() => setOpen(false)} hasCloseButton={false}>
      <div ref={panelRef} className="w-[600px] max-w-[92vw] flex flex-col surface-base">
        {/* Search input */}
        <div className="flex items-center gap-12 px-16 py-12 border-b border-subtle">
          <Icon name="search" width={20} height={20} className="text-faded shrink-0" />
          <input
            ref={inputRef}
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={onInputKeyDown}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            placeholder={t('commandPalette.placeholder', { defaultValue: 'Alle Bereiche durchsuchen…' })}
            className="flex-1 bg-transparent border-none outline-none typo-text-14 text-full placeholder:text-faded"
          />
          <KbdChip>ESC</KbdChip>
        </div>

        {/* Body */}
        <div className="max-h-[420px] overflow-y-auto py-8">
          {!enabled && recentItems.length > 0 && (
            <div>
              <div className="px-16 py-6 typo-text-12 text-faded uppercase tracking-wide">
                {t('commandPalette.recent', { defaultValue: 'Zuletzt besucht' })}
              </div>
              {recentItems.map((item, i) => renderRow(item, `recent-${i}`))}
            </div>
          )}

          {!enabled && recentItems.length === 0 && (
            <div className="px-16 py-24 text-center typo-text-14 text-faded">
              {t('commandPalette.hint', { defaultValue: 'Tippen, um alle Bereiche zu durchsuchen.' })}
            </div>
          )}

          {enabled && navMatches.length > 0 && (
            <div className="mb-4">
              <div className="px-16 py-6 typo-text-12 text-faded uppercase tracking-wide">
                {t('commandPalette.categories.pages', { defaultValue: 'Seiten' })}
              </div>
              {navMatches.map((item, i) => renderRow(item, `page-${i}`))}
            </div>
          )}

          {enabled &&
            categories.map(({ category, hits }) => (
              <div key={category.key} className="mb-4">
                <div className="px-16 py-6 typo-text-12 text-faded uppercase tracking-wide">
                  {t(`commandPalette.categories.${category.key}`, { defaultValue: category.key })}
                </div>
                {hits.map(hit =>
                  renderRow(
                    { to: hit.to, label: hit.label, subtitle: hit.subtitle, icon: category.icon },
                    `${category.key}-${hit.id}`,
                  ),
                )}
              </div>
            ))}

          {enabled && !isFetching && navMatches.length === 0 && categories.length === 0 && (
            <div className="px-16 py-24 text-center typo-text-14 text-faded">
              {t('commandPalette.noResults', { defaultValue: 'Keine Ergebnisse gefunden' })}
            </div>
          )}
        </div>

        {/* Footer hints */}
        <div className="flex items-center gap-16 px-16 py-10 border-t border-subtle typo-text-12 text-faded">
          <span className="flex items-center gap-6">
            <KbdChip>↑↓</KbdChip>
            {t('commandPalette.navigate', { defaultValue: 'Navigieren' })}
          </span>
          <span className="flex items-center gap-6">
            <KbdChip>↵</KbdChip>
            {t('commandPalette.select', { defaultValue: 'Öffnen' })}
          </span>
          <span className="flex items-center gap-6">
            <KbdChip>esc</KbdChip>
            {t('commandPalette.close', { defaultValue: 'Schließen' })}
          </span>
        </div>
      </div>
    </Modal>
  );
};

export default CommandPalette;
